import { Anchor, Group, Progress, SimpleGrid, Stack, Text } from '@mantine/core'
import type { Patient } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import {
  SDRStatus,
  sdrStatusToMetadata,
} from '@seen/core/models/tasks/sdr/utils'
import { createReference } from '@seen/core/references'
import { Badge, type BadgeProps, cn } from '@seen/ui'
import type { JSX } from 'react'
import { SDRReviseDueDate } from '#src/components/sdr/display/SDRReviseDueDate.tsx'
import { SDRTimeRemainingWithTooltip } from '#src/components/sdr/display/SDRTimeRemainingWithTooltip.tsx'
import type { SDRDisplayStep } from '#src/components/sdr/display/utils.ts'

const SDR_STATUS_COLOR: Record<SDRStatus, BadgeProps['color']> = {
  [SDRStatus.REQUESTED]: 'gray',
  [SDRStatus.IN_REVIEW]: 'gray',
  [SDRStatus.APPROVED]: 'teal',
  [SDRStatus.DENIED]: 'red',
  [SDRStatus.PARTIALLY_DENIED]: 'yellow',
  [SDRStatus.PARTIALLY_APPROVED]: 'yellow',
  [SDRStatus.WITHDRAWN]: 'gray',
  [SDRStatus.IN_APPEAL]: 'orange',
}

type StepProps = {
  maxStep: SDRDisplayStep
  selectedStep: SDRDisplayStep
  setSelectedStep: (
    step:
      | SDRDisplayStep
      | null
      | ((prev: SDRDisplayStep | null) => SDRDisplayStep | null)
  ) => void
}

type Props = {
  sdr: SDR
  patient: Patient
  stepProps: StepProps
}

export function SDRProgress({ sdr, patient, stepProps }: Props): JSX.Element {
  invariant(sdr.id, 'SDR must have an ID')
  invariant(sdr.createdAt, 'SDR must have a creation date')

  const sdrStatus = sdr.status ?? SDRStatus.REQUESTED
  const isEditable =
    sdrStatus === SDRStatus.REQUESTED || sdrStatus === SDRStatus.IN_REVIEW
  const dateLabel =
    sdrStatus === SDRStatus.REQUESTED
      ? 'Needs to be reviewed by'
      : 'Decision due by'

  return (
    <Stack className="grow">
      <div className="flex justify-between">
        <Group>
          <Text className="font-semibold">Step: {stepProps.maxStep}/4</Text>
          <Badge
            className="uppercase select-none"
            color={SDR_STATUS_COLOR[sdrStatus]}
            variant="outline"
          >
            {sdrStatusToMetadata[sdrStatus].label}
          </Badge>
        </Group>
        {stepProps.maxStep !== 4 && (
          <Group gap="xs">
            {isEditable && (
              <SDRReviseDueDate
                sdr={sdr}
                subject={createReference(patient)}
                currentDueDate={sdr.decisionNotificationDate?.toISOString()}
                dateLabel={dateLabel}
              />
            )}
            <SDRTimeRemainingWithTooltip sdr={sdr} />
          </Group>
        )}
      </div>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <SDRProgressItem
          label="Create Initial SDR"
          itemStep={1}
          stepProps={stepProps}
        />
        <SDRProgressItem
          label="Review, Assess, Set Disposition"
          itemStep={2}
          stepProps={stepProps}
        />
        <SDRProgressItem
          label="Notify Requester"
          itemStep={3}
          stepProps={stepProps}
        />
        <SDRProgressItem
          label={sdr.progressLabel}
          itemStep={4}
          stepProps={stepProps}
        />
      </SimpleGrid>
    </Stack>
  )
}

type ProgressItemProps = {
  label: string
  itemStep: SDRDisplayStep
  stepProps: StepProps
}

function SDRProgressItem({
  label,
  itemStep,
  stepProps,
}: ProgressItemProps): JSX.Element {
  const active = itemStep <= stepProps.selectedStep
  const enabled = itemStep <= stepProps.maxStep
  return (
    <Stack className="grow gap-1">
      <Progress value={active ? 100 : 0} className="h-2" />
      <Anchor
        className={cn({
          'cursor-default text-gray-500 no-underline': !enabled,
          'underline hover:no-underline': enabled,
        })}
        size="sm"
        onClick={
          enabled
            ? () => {
                stepProps.setSelectedStep(
                  stepProps.selectedStep === itemStep ? null : itemStep
                )
              }
            : undefined
        }
      >
        {label}
      </Anchor>
    </Stack>
  )
}
